const post = {
  slug: 'writing-a-script-for-narration',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'Writing for the ear, not the page',
  description:
    'A script is going to be spoken. That changes the punctuation, the numbers, the sentence length and the vocabulary — and most drafts ignore all four.',
  keywords: [
    'writing for narration', 'voiceover script', 'script for tts',
    'writing for audio', 'narration script tips', 'spoken script',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/writing-a-script-for-narration.webp',
    alt: 'A paragraph marked up with numerals rewritten as words and long sentences split at breath points.',
  },
  lede:
    'Prose written to be read silently and prose written to be spoken aloud are different crafts. The gap is audible within about ten seconds, and almost all of it comes from four fixable habits.',
  sections: [
    {
      heading: 'Spell out anything a reader converts silently',
      body: [
        {
          type: 'para',
          text: 'A reader sees "1954" and thinks "nineteen fifty-four" without noticing. A narrator — human or synthetic — has to decide, and the decision is frequently wrong.',
        },
        {
          type: 'list',
          items: [
            '**Years**: "nineteen fifty-four", not "1954".',
            '**Large numbers**: "about three hundred thousand", not "~300,000".',
            '**Symbols**: "roughly forty percent", not "~40%". "Twelve dollars", not "$12".',
            '**Units and abbreviations**: "kilometres", not "km". "For example", not "e.g.".',
            '**Ranges**: "between nineteen sixty and nineteen sixty-five" — a hyphen is not pronounceable.',
          ],
        },
        {
          type: 'para',
          text: 'This is the single highest-yield habit on the list, because getting it wrong is not a subtle quality issue — it produces an audibly broken line that has to be re-recorded.',
        },
      ],
    },
    {
      heading: 'Sentences need breath points',
      body: [
        {
          type: 'para',
          text: 'A sentence with three subordinate clauses is fine on a page, where the reader controls the pace. Spoken, it is a run that has nowhere to breathe, and the listener loses the thread before the verb arrives.',
        },
        {
          type: 'example',
          bad: 'The company, which had been founded eleven years earlier by two engineers who had previously worked together at a competitor that collapsed in the same downturn, filed for bankruptcy in March.',
          good: 'The company filed for bankruptcy in March. It had been founded eleven years earlier by two engineers — who had met at a competitor that collapsed in the same downturn.',
          note: 'Same information, same length. The second one can be said.',
        },
        {
          type: 'para',
          text: 'The mechanical test: read it aloud in one breath. If you cannot, split it.',
        },
      ],
    },
    {
      heading: 'Punctuation is timing, not grammar',
      body: [
        {
          type: 'para',
          text: 'In a narration script, punctuation is closer to musical notation than to grammar. A comma is a short pause, a full stop is a longer one, and a dash is a beat.',
        },
        {
          type: 'para',
          text: 'This means it is entirely legitimate to punctuate for rhythm rather than correctness. A sentence fragment is fine. Starting with "And" is fine. Both read as natural speech, which is the target.',
        },
        {
          type: 'para',
          text: 'Two things to avoid: semicolons, which have no audible equivalent, and parentheses, which a narrator cannot voice — the aside either gets its own sentence or gets cut.',
        },
      ],
    },
    {
      heading: 'Vocabulary the listener cannot re-read',
      body: [
        {
          type: 'para',
          text: 'A reader who meets an unfamiliar word can stop and look at it again. A listener gets one pass at the speed you chose.',
        },
        {
          type: 'para',
          text: 'That does not mean simplifying — using the term practitioners actually use is one of the things that makes a script specific and credible. It means introducing it properly: say the term, then say what it means in plain words, immediately, once. After that you can use it freely.',
        },
        {
          type: 'para',
          text: 'Also watch homophones. Spoken, "their" and "there" are identical, and a sentence that relies on the distinction becomes ambiguous.',
        },
      ],
    },
    {
      heading: 'Read the whole thing aloud',
      body: [
        {
          type: 'para',
          text: 'This is the entire quality-control process and it takes as long as the video does.',
        },
        {
          type: 'para',
          text: 'Every place you stumble is a place the narration will stumble. Every sentence you instinctively rephrase while reading is one to rewrite as you rephrased it — your mouth is a better editor than your eye for this purpose.',
        },
        {
          type: 'callout',
          title: 'A second use for it',
          text: 'Reading aloud also times the script. Your read is a reasonable proxy for the finished runtime, which is more reliable than a word count. See [how long a script should be](/blogs/how-long-should-a-youtube-script-be).',
        },
      ],
    },
    {
      heading: 'If a synthetic voice is reading it',
      body: [
        {
          type: 'para',
          text: 'Everything above matters more, not less. A human narrator silently corrects a badly written line; a synthetic one reads exactly what is there.',
        },
        {
          type: 'para',
          text: 'Two additions: proper nouns and technical terms are the most common mispronunciations, so listen to those specifically. And overly long sentences degrade worse in synthesis than in human reading, because the intonation flattens across the clause.',
        },
        {
          type: 'para',
          text: 'If you need subtitles from the finished audio, the [subtitle generator](/free-tools/subtitle-generator) produces an SRT in the browser without uploading the file anywhere.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Should I write in a conversational tone?',
      a: 'Write in a tone you can say out loud without feeling odd. That is usually more conversational than written prose and less chatty than deliberately casual writing, which tends to read as forced when spoken.',
    },
    {
      q: 'How do I handle quotes in narration?',
      a: 'Attribute before the quote rather than after — the listener needs to know who is speaking as they hear it, not once it has finished. Keep quoted passages short for the same reason.',
    },
  ],
  related: ['script-pacing-and-shot-length', 'youtube-script-writing', 'how-long-should-a-youtube-script-be'],
};

export default post;
